import { useMutation, useQueryClient } from '@tanstack/react-query'
import { LogOut, User as UserIcon } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router'

import { api } from '@/api/client'
import { keys, useChangePassword, useLogout, useMe } from '@/api/hooks'
import type { User } from '@/api/types'
import { Button, Input, Skeleton, toast } from '@/components/ui'
import { fieldErrorOf, generalErrorOf } from '@/features/auth/authErrors'
import { PasswordInput } from '@/features/auth/PasswordInput'

import { Callout } from './Callout'
import { SettingsSection } from './SettingsSection'
import { SettingsDisclosure } from './SettingsDisclosure'

// Must match Django's MinimumLengthValidator, or the client waves through a
// password the server then rejects.
const MIN_PASSWORD_LENGTH = 10

/** Local to this section: the profile PATCH has exactly one caller. */
function useUpdateProfile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { display_name: string }) => api.patch<User>('/auth/me', body),
    onSuccess: (user) => {
      queryClient.setQueryData(keys.me, user)
      void queryClient.invalidateQueries({ queryKey: keys.me })
    },
  })
}

function ReadOnlyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-text">{label}</span>
      <span className="flex h-11 items-center rounded-lg border border-border bg-surface px-3 text-base text-text">
        {value}
      </span>
    </div>
  )
}

function AccountSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-11 w-full" />
      </div>
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-11 w-full" />
      </div>
      <Skeleton className="h-11 w-32" />
    </div>
  )
}

export function AccountSection() {
  const { data: user, isPending } = useMe()
  const profile = useUpdateProfile()
  const changePassword = useChangePassword()
  const logout = useLogout()
  const navigate = useNavigate()

  // `null` means "follow the server". Set once the user types, cleared on save,
  // so a background refetch never yanks the field out from under them.
  const [nameDraft, setNameDraft] = useState<string | null>(null)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  if (isPending) {
    return (
      <SettingsSection icon={UserIcon} title="Account">
        <AccountSkeleton />
      </SettingsSection>
    )
  }

  if (!user) {
    return (
      <SettingsSection icon={UserIcon} title="Account">
        <Callout tone="warning">Your session has ended. Sign in again to manage your account.</Callout>
      </SettingsSection>
    )
  }

  const savedName = user.display_name
  const name = nameDraft ?? savedName
  const nameDirty = name.trim() !== savedName
  const nameError = fieldErrorOf(profile.error, 'display_name')
  const profileBanner = generalErrorOf(profile.isError ? profile.error : null, ['display_name'])

  function handleNameSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const trimmed = name.trim()
    if (!trimmed || !nameDirty || profile.isPending) return
    profile.mutate(
      { display_name: trimmed },
      {
        onSuccess: () => {
          setNameDraft(null)
          toast.success('Display name updated')
        },
      },
    )
  }

  const passwordTooShort = newPassword.length > 0 && newPassword.length < MIN_PASSWORD_LENGTH
  const passwordMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword
  const passwordUnchanged = newPassword.length > 0 && newPassword === currentPassword
  const canChangePassword =
    currentPassword.length > 0 &&
    newPassword.length >= MIN_PASSWORD_LENGTH &&
    newPassword === confirmPassword &&
    !passwordUnchanged

  const passwordBanner = generalErrorOf(changePassword.isError ? changePassword.error : null, [
    'current_password',
    'new_password',
  ])

  function handlePasswordSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!canChangePassword || changePassword.isPending) return
    changePassword.mutate(
      { current_password: currentPassword, new_password: newPassword },
      {
        onSuccess: () => {
          setCurrentPassword('')
          setNewPassword('')
          setConfirmPassword('')
          changePassword.reset()
          toast.success('Password changed', 'Use it the next time you sign in.')
        },
      },
    )
  }

  function handleSignOut() {
    logout.mutate(undefined, {
      onSuccess: () => navigate('/login', { replace: true }),
      onError: () =>
        toast.error('Could not sign out', 'Check your connection and try again.'),
    })
  }

  return (
    <SettingsSection
      icon={UserIcon}
      title="Account"
    >
      <div className="flex flex-col gap-4">
        <ReadOnlyRow label="Email" value={user.email} />

        <form onSubmit={handleNameSubmit} noValidate className="flex flex-col gap-3">
          {profileBanner ? (
            <Callout tone="danger" announce>
              {profileBanner}
            </Callout>
          ) : null}

          <Input
            label="Display name"
            value={name}
            onChange={(event) => setNameDraft(event.target.value)}
            autoComplete="nickname"
            maxLength={80}
            disabled={profile.isPending}
            error={nameError}
          />

          <div className="flex items-center gap-2">
            <Button
              type="submit"
              loading={profile.isPending}
              disabled={!nameDirty || name.trim().length === 0}
            >
              Save name
            </Button>
            {nameDirty ? (
              <><Button variant="ghost" onClick={() => setNameDraft(null)}>Discard</Button><span className="text-sm text-text-muted">Unsaved changes</span></>
            ) : null}
          </div>
        </form>

        <SettingsDisclosure title="Change password">
          <form onSubmit={handlePasswordSubmit} noValidate className="flex flex-col gap-3">
            {passwordBanner ? (
              <Callout tone="danger" announce>
                {passwordBanner}
              </Callout>
            ) : null}

            <PasswordInput
              label="Current password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete="current-password"
              disabled={changePassword.isPending}
              error={fieldErrorOf(changePassword.error, 'current_password')}
            />

            <PasswordInput
              label="New password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
              disabled={changePassword.isPending}
              error={
                fieldErrorOf(changePassword.error, 'new_password') ??
                (passwordTooShort
                  ? `Use at least ${MIN_PASSWORD_LENGTH} characters.`
                  : passwordUnchanged
                    ? 'Choose a password different from your current one.'
                    : undefined)
              }
              hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
            />

            <PasswordInput
              label="Confirm new password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete="new-password"
              disabled={changePassword.isPending}
              error={passwordMismatch ? 'The two passwords do not match.' : undefined}
            />

            <div>
              <Button type="submit" loading={changePassword.isPending} disabled={!canChangePassword}>
                Change password
              </Button>
            </div>
          </form>
        </SettingsDisclosure>

        <hr className="border-border" />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button variant="secondary" loading={logout.isPending} onClick={handleSignOut}>
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Sign out
          </Button>
        </div>
      </div>
    </SettingsSection>
  )
}
